import React from 'react';
import PropTypes from 'prop-types';
import { createUseStyles, withTheme } from '@glu/theming';
import {
  XIcon,
  ExclamationTriangleAshIcon, ExclamationCircleAshIcon, CheckmarkCircleAshIcon, InfoCircleAshIcon,
  CheckmarkCircleIcon, ExclamationCircleIcon, ExclamationTriangleIcon, InfoCircleIcon
} from '@glu/icons-react';
import { AccessibilityText } from '@glu/utilities-react';
import locale from '@glu/locale';
import classnames from 'classnames';
import styles from './ModalHeader.styles';

const useStyles = createUseStyles(styles);

const icons = {
  ash: {
    success: CheckmarkCircleAshIcon,
    error: ExclamationTriangleAshIcon,
    warning: ExclamationCircleAshIcon,
    info: InfoCircleAshIcon,
    close: XIcon
  },
  legacy: {
    success: CheckmarkCircleIcon,
    error: ExclamationCircleIcon,
    warning: ExclamationTriangleIcon,
    info: InfoCircleIcon,
    close: XIcon
  }
};

const ModalHeader = (props) => {
  const {
    type, btnCloseEnabled, onClose, useHeaderElem, header, secondaryHeader, ariaLabelledby,
    ariaDescribedby, theme
  } = props;
  const classes = useStyles(props);
  const Icon = (icons[theme.name] && icons[theme.name][type?.toLowerCase()]) || undefined;
  const CloseIcon = icons[theme.name].close;
  const primaryHeaderClasses = classnames(
    classes.primaryHeader,
    { [classes.hasSecondaryHeader]: !!secondaryHeader }
  );
  return (
    <div className={classnames(
      classes.header,
      classes[type],
      { [classes.hasSecondaryHeader]: !!secondaryHeader }
    )}
    >
      {Icon && (
        <Icon
          title={type.toUpperCase()}
          className={classnames(
            classes.icon,
            { [classes.hasHeader]: !!header, [classes.hasSecondaryHeader]: !!secondaryHeader }
          )}
        />
      )}
      {btnCloseEnabled && (
        <button
          type="button"
          className={classnames(
            classes.btnClose,
            { [classes.hasSecondaryHeader]: !!secondaryHeader }
          )}
          onClick={onClose}
        >
          <AccessibilityText>
            {locale.get('closeX')}
          </AccessibilityText>
          <CloseIcon width={16} height={16} />
        </button>
      )}
      {header && (
        <div className={classes.headerText}>
          {useHeaderElem
            ? <h2 className={primaryHeaderClasses} id={ariaLabelledby}>{header}</h2>
            : <div className={primaryHeaderClasses} id={ariaLabelledby}>{header}</div>}
          {secondaryHeader
              && (
                <>
                  <div className={classes.secondaryHeaderDivider} />
                  <div
                    className={classes.secondaryHeader}
                    id={ariaDescribedby}
                  >
                    {secondaryHeader}
                  </div>
                </>
              )}
        </div>
      )}

    </div>
  );
};

ModalHeader.propTypes = {
  /** Toggle if 'X' button in top right is displayed */
  btnCloseEnabled: PropTypes.bool,
  /** Headline text */
  header: PropTypes.string,
  /** Callback for close 'X' button */
  onClose: PropTypes.func,
  /** Text for secondary hard. Appears inline with regular header if present */
  secondaryHeader: PropTypes.string,
  /** Message type. Warning, Info, Error, Success */
  type: PropTypes.string,
  /**
   * For cases where this is a visible modal title present.
   * Set this value to the id of the title element
   */
  ariaLabelledby: PropTypes.string,
  /**
   * For cases where the dialog contains additional descriptive text
   * besides the dialog title use this attribute to define it.
   * Set this value to the id of the description element.
   */
  ariaDescribedby: PropTypes.string,
  /** Default element wrapping modal title is div.
   * Set this to true to use <h2> instead)
   */
  useHeaderElem: PropTypes.bool,
  theme: PropTypes.shape({
    name: PropTypes.string
  })
};

ModalHeader.defaultProps = {
  btnCloseEnabled: true,
  header: undefined,
  onClose: undefined,
  secondaryHeader: undefined,
  type: undefined,
  ariaLabelledby: undefined,
  ariaDescribedby: undefined,
  useHeaderElem: false,
  theme: { name: 'ash' }
};

export default withTheme(ModalHeader);
